import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

/*
 * LURVENZA · Vista rápida
 * El modal no descarga la PDP completa: pide la plantilla alternativa templates/product.lz-quick-view.json
 * (sin layout, solo la sección nativa product-information con galería, precio, variantes y compra).
 * Las respuestas se comparten entre todas las cards de la página (un mismo producto puede estar en varias
 * grillas) y se guardan como promesa, así el prefetch del hover y el clic reutilizan la misma petición.
 */
const LZ_QUICK_VIEW_TEMPLATE = 'lz-quick-view';
const LZ_SHIPPING_NOTE = 'Envío gratis a todo Chile';
const LZ_CUSTOM_NOTE = 'Pieza personalizada: eliges los detalles en la ficha del producto.';

/** @type {Map<string, Promise<Document | null>>} */
const lzQuickViewCache = new Map();

/**
 * @param {string} productPageUrl
 * @returns {string} La URL de la plantilla de Vista rápida (conserva ?variant=).
 */
function lzQuickViewUrl(productPageUrl) {
  const url = new URL(productPageUrl, window.location.origin);
  url.searchParams.set('view', LZ_QUICK_VIEW_TEMPLATE);
  return url.pathname + url.search;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  #isOpening = false;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    lzQuickViewCache.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Evita dobles aperturas si el cliente hace doble clic mientras llega la respuesta.
    if (this.#isOpening) return;
    this.#isOpening = true;
    this.setAttribute('aria-busy', 'true');

    try {
      const html = await this.fetchProductPage(currentUrl);
      const gridElement = html?.querySelector('[data-product-grid-content]');

      // Sin contenido no abrimos un modal vacío: el cliente sigue a la ficha completa.
      if (!gridElement) {
        if (currentUrl) window.location.href = currentUrl;
        return;
      }

      // El documento cacheado nunca se modifica: siempre se trabaja sobre copias.
      const productGrid = this.#prepareQuickViewContent(/** @type {Element} */ (gridElement.cloneNode(true)));
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);

      this.#openQuickAddModal();
    } finally {
      this.#isOpening = false;
      this.removeAttribute('aria-busy');
    }
  };

  /**
   * LURVENZA · Adapta el contenido de la plantilla lz-quick-view antes de mostrarlo.
   * - Personalizados (tag lz-custom, ver snippets/quick-add.liquid): se quitan variantes y formulario de compra,
   *   porque la personalización (Globo) solo se completa en la ficha. El CTA lleva a la PDP.
   * - Estándar: se agrega una línea de envío y el enlace "Ver detalles".
   * @param {Element} productGrid
   * @returns {Element}
   */
  #prepareQuickViewContent(productGrid) {
    const isCustom = this.dataset.lzCustom === 'true';
    const productUrl = productGrid.querySelector('.view-product-title a')?.getAttribute('href') || this.productPageUrl;

    productGrid.querySelectorAll('script[type="application/ld+json"]').forEach((script) => script.remove());
    productGrid.setAttribute('data-lz-quick-view', isCustom ? 'custom' : 'standard');

    if (isCustom) {
      productGrid.querySelectorAll('variant-picker, .buy-buttons-block, product-form-component').forEach((el) => el.remove());
    }

    const actions = document.createElement('div');
    // view-more-details__wrapper: clase que el CSS nativo del modal deja visible dentro de .product-details.
    actions.className = 'view-more-details__wrapper lz-quick-view__actions';

    const note = document.createElement('p');
    note.className = 'lz-quick-view__note';
    note.textContent = isCustom ? LZ_CUSTOM_NOTE : LZ_SHIPPING_NOTE;

    const link = document.createElement('a');
    link.href = productUrl;
    link.dataset.lzQuickViewLink = '';

    if (isCustom) {
      link.className = 'button lz-quick-view__cta';
      link.textContent = 'Personalizar';
      actions.append(note, link);
    } else {
      link.className = 'lz-quick-view__details';
      link.textContent = 'Ver detalles';
      actions.append(link, note);
    }

    const details = productGrid.querySelector('.product-details .group-block-content') || productGrid.querySelector('.product-details');
    details?.append(actions);

    return productGrid;
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.refs?.dialog?.setAttribute('aria-label', `Vista rápida: ${this.dataset.productTitle || ''}`);
    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the Quick View content (template lz-quick-view) for a product.
   * Also used by product-card.js as a hover/focus prefetch, so it never rejects:
   * failed requests resolve to null and are dropped from the cache to allow a retry.
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  fetchProductPage(productPageUrl) {
    if (!productPageUrl) return Promise.resolve(null);

    const requestUrl = lzQuickViewUrl(productPageUrl);
    let request = lzQuickViewCache.get(requestUrl);

    if (!request) {
      request = fetch(requestUrl)
        .then((response) => {
          if (!response.ok) throw new Error(`Failed to fetch quick view: HTTP error ${response.status}`);
          return response.text();
        })
        .then((responseText) => new DOMParser().parseFromString(responseText, 'text/html'))
        .catch((error) => {
          lzQuickViewCache.delete(requestUrl);
          console.warn('[quick-add] Quick view request failed:', error);
          return null;
        });
      lzQuickViewCache.set(requestUrl, request);
    }

    return request;
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // La plantilla lz-quick-view ya trae solo lo necesario y en el orden de la hoja inferior (bottom sheet),
    // así que no hace falta la reestructuración móvil que Horizon aplica sobre la PDP completa.
    if (isMobileBreakpoint() && !productGrid.hasAttribute('data-lz-quick-view')) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);
    modalContent.setAttribute('data-lz-quick-view', productGrid.getAttribute('data-lz-quick-view') || '');

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {PerformanceObserver | null} */
  #cartAddObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener(DialogOpenEvent.eventName, this.#watchCartAdds, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#stopWatchingCartAdds();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * LURVENZA · UpCart intercepta el botón "Agregar al carrito" y hace su propio POST a /cart/add.js
   * (igual que en la PDP), así que el evento nativo cartLinesUpdate no llega y handleCartUpdate no cierra
   * el modal. Mientras el modal está abierto se observa esa petición (sin tocar fetch) y, si respondió
   * bien, se cierra para dejar a la vista el carrito de UpCart.
   */
  #watchCartAdds = () => {
    if (typeof PerformanceObserver === 'undefined') return;

    this.#stopWatchingCartAdds();
    this.#cartAddObserver = new PerformanceObserver((list) => {
      const entry = /** @type {PerformanceResourceTiming | undefined} */ (
        list.getEntries().find((item) => item.name.includes('/cart/add'))
      );
      if (!entry) return;

      // responseStatus no existe en todos los navegadores; si falta se asume éxito (UpCart ya muestra el carrito).
      const status = /** @type {PerformanceResourceTiming & { responseStatus?: number }} */ (entry).responseStatus;
      if (status && status >= 400) return;

      this.#stopWatchingCartAdds();
      this.closeDialog();
    });
    this.#cartAddObserver.observe({ type: 'resource' });
  };

  #stopWatchingCartAdds() {
    this.#cartAddObserver?.disconnect();
    this.#cartAddObserver = null;
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
        // LURVENZA · "Ver detalles" / "Personalizar" siguen a la variante elegida.
        this.querySelectorAll('[data-lz-quick-view-link]').forEach((link) => {
          if (link instanceof HTMLAnchorElement) link.href = anchorElement.href;
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    this.#stopWatchingCartAdds();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
